import { useMemo } from 'react';
import reset from '~/assets/icons/reset_2.svg';
import { ButtonColors } from '~/components/ui/Button';
import { IShopPreviewProps, VariationMode } from '~/components/ui/ShopPreview';
import { rage, useObservable } from '~/rage';
import {
	VehiclePaintType,
	VehicleTuningCategory,
	VehicleWheelTireProfile,
	WalletType,
} from '~/shared';
import {
	CategoryTypes,
	colorSelectItems,
} from '../components/ColorSelect/ColorSelect.model';
import { getResetTuningIndex } from '../components/Main/Main.model';
import {
	getWheelSettingsTotalPrice,
	isDefaultValues,
} from '../components/WheelSettings/WheelSettings.model';
import {
	setActiveVariant,
	TuningStudioStage,
	useLayoutDispatch,
	useLayoutSelector,
} from '../redux';
import {
	getActiveVariant,
	getEffectiveTuningSelection,
} from '../utils/resolveTuningShopVariant';
import { useBuy } from './useBuy';
import { useResetTuning } from './useResetTuning';
import { useTuningCategories } from './useTuningCategories';

const useShopPreview = (): IShopPreviewProps => {
	const dispatch = useLayoutDispatch();
	const windowTints = useObservable(
		rage.vehiclePaintShop.getVehicleWindowTints(),
	);
	const rimsColors = useObservable(
		rage.vehiclePaintShop.getVehicleRimsColors(),
	);
	const rimsColorsPrice = useObservable(
		rage.vehiclePaintShop.getVehicleRimsColorPrice(),
	);
	const paintGroups = useObservable(
		rage.vehiclePaintShop.getVehiclePaintTypes(),
	);
	const { shopItems, nonExteriorCategories, ownedWheelSettings } =
		useTuningCategories();
	const donateItems = useObservable(rage.inventoryDonate.getDonateOwnedItems());
	const wheelsSetupPrice = useObservable(
		rage.vehicleTuningShop.getWheelsSetupPrice(),
	);
	const wheelTireProfile = useObservable(
		rage.vehicleTuningShop.getWheelTireProfile(),
	);
	const tireProfileChangePrice = useObservable(
		rage.vehicleTuningShop.getTireProfileChangePrice(),
	);
	const activeStage = useLayoutSelector(
		(state) => state.tuningStudio.activeStage,
	);
	const activeIndex = useLayoutSelector(
		(state) => state.tuningStudio.activeIndex,
	);
	const activeTuningCategory = useLayoutSelector(
		(state) => state.tuningStudio.activeTuningCategory,
	);
	const activeTuningSubcategory = useLayoutSelector(
		(state) => state.tuningStudio.activeTuningSubcategory,
	);
	const activeVariants = useLayoutSelector(
		(state) => state.tuningStudio.activeVariants,
	);
	const wheelSettings = useLayoutSelector(
		(state) => state.tuningStudio.wheelSettings,
	);
	const selectedTireProfile = useLayoutSelector(
		(state) => state.tuningStudio.tireProfile,
	);
	const rimsColor = useLayoutSelector((state) => state.colorPicker.rimsColor);
	const activePaintGroup = useLayoutSelector(
		(state) => state.colorPicker.paintGroup,
	);
	const { handleBuy } = useBuy();
	const { confirmReset } = useResetTuning();

	const filteredShopItems = useMemo(() => {
		if (activeTuningSubcategory) {
			return shopItems.filter(
				(item) => item.subcategoryId === activeTuningSubcategory,
			);
		}
		if (activeTuningCategory) {
			return shopItems.filter(
				(item) => item.categoryId === activeTuningCategory,
			);
		}
		return [];
	}, [shopItems, activeTuningCategory, activeTuningSubcategory]);

	const activeShopItem = useMemo(() => {
		return filteredShopItems[activeIndex];
	}, [filteredShopItems, activeIndex]);

	const currentItem = useMemo(() => {
		return colorSelectItems[activeIndex];
	}, [activeIndex]);

	const isPaintingWorks = currentItem?.type === CategoryTypes.PaintingWorks;
	const isPaintingInterior =
		currentItem?.type === CategoryTypes.PaintingInterior;
	const isPaintingWheels = currentItem?.type === CategoryTypes.PaintingWheels;
	const isBrakes = currentItem?.type === CategoryTypes.Brakes;

	const totalWheelPrice = useMemo(() => {
		let price = 0;
		if (ownedWheelSettings) {
			price = getWheelSettingsTotalPrice(
				wheelSettings,
				wheelsSetupPrice,
				ownedWheelSettings,
			);
		}
		const currentTireProfile =
			wheelTireProfile ?? VehicleWheelTireProfile.MEDIUM;
		if (selectedTireProfile !== currentTireProfile) {
			price += tireProfileChangePrice ?? 0;
		}
		return price;
	}, [
		wheelSettings,
		wheelsSetupPrice,
		ownedWheelSettings,
		wheelTireProfile,
		selectedTireProfile,
		tireProfileChangePrice,
	]);

	if (activeStage === TuningStudioStage.MAIN) {
		const resetIndex = getResetTuningIndex(nonExteriorCategories.length);
		if (activeIndex === resetIndex) {
			return {
				visible: true,
				name: 'Сброс в сток',
				description:
					'Весь ваш тюнинг на автомобиле будет сброшен до заводских настроек.',
				bottomTitle:
					'При сбросе автомобиля в сток, весь ваш тюнинг с автомобиля будет удалён.',
				price: 0,
				hide: { price: true },
				customButtons: [
					{
						color: ButtonColors.Cancel,
						text: 'Сбросить в сток',
						icon: reset,
						onClick: confirmReset,
					},
				],
				handlePayByCash: () => {},
				handlePayByCard: () => {},
			};
		}

		const isWheelSettingsSelected =
			activeIndex > 2 &&
			nonExteriorCategories[activeIndex - 3]?.id ===
				VehicleTuningCategory.WHEEL_SETTINGS;

		if (isWheelSettingsSelected && ownedWheelSettings) {
			const currentTireProfile =
				wheelTireProfile ?? VehicleWheelTireProfile.MEDIUM;
			const hasChanges =
				!isDefaultValues(wheelSettings, ownedWheelSettings) ||
				selectedTireProfile !== currentTireProfile;
			return {
				visible: hasChanges,
				name: 'Настройка колёс',
				price: totalWheelPrice,
				handlePayByCash: () => handleBuy(WalletType.Cash),
				handlePayByCard: () => handleBuy(WalletType.Bank),
			};
		}

		return {
			visible: false,
			name: '',
			price: 0,
			handlePayByCash: () => {},
			handlePayByCard: () => {},
		};
	}

	// Тюнинг
	if (activeStage === TuningStudioStage.TUNING_MENU) {
		if (!activeShopItem) {
			return {
				visible: false,
				name: '',
				price: 0,
				handlePayByCash: () => {},
				handlePayByCard: () => {},
			};
		}

		const effective = getEffectiveTuningSelection(
			activeShopItem,
			activeVariants,
		);
		const variantCount = activeShopItem.variants?.length ?? 0;
		const showVariation =
			activeShopItem.categoryId !== VehicleTuningCategory.KITS &&
			variantCount > 1;
		const vIdx = showVariation
			? Math.min(
					Math.max(0, activeVariants[activeShopItem.id] ?? 0),
					variantCount - 1,
			  )
			: 0;
		const activeVariant = getActiveVariant(activeShopItem, activeVariants);
		const variantSubtitle = activeVariant?.name
			? activeVariant.name.replace(effective.parentName, '').trim()
			: undefined;

		const variationProps = showVariation
			? {
					variationTitle: 'Вариант',
					variationMode: VariationMode.Variant,
					variationIndex: vIdx,
					maxVariantIndex: variantCount,
					onVariationChange: (newIndex: number) =>
						dispatch(
							setActiveVariant({
								itemId: activeShopItem.id,
								index: newIndex,
							}),
						),
			  }
			: {};

		const isOwned = rage.vehicleTuningShop.getIsOwned(effective.id);
		const donateCount =
			donateItems.find((item) => item.id === effective.id)?.count ?? 0;

		if (donateCount > 0) {
			return {
				visible: true,
				name: effective.parentName,
				subtitle: variantSubtitle,
				stock: donateCount,
				hide: {
					price: true,
				},
				price: 0,
				handlePayByCash: () => {},
				handlePayByCard: () => {},
				...variationProps,
			};
		}

		if (isOwned) {
			return {
				visible: true,
				name: effective.parentName,
				subtitle: variantSubtitle,
				stock: 1,
				hide: {
					price: true,
				},
				price: 0,
				handlePayByCash: () => {},
				handlePayByCard: () => {},
				...variationProps,
			};
		}

		return {
			visible: true,
			name: effective.parentName,
			subtitle: variantSubtitle,
			price: effective.price,
			handlePayByCash: () => handleBuy(WalletType.Cash),
			handlePayByCard: () => handleBuy(WalletType.Bank),
			...variationProps,
		};
	}

	// Тонировка
	if (activeStage === TuningStudioStage.TINTING) {
		const item = windowTints[activeIndex];

		return {
			visible: item !== undefined,
			name: item?.name ?? '',
			price: item?.price || 0,
			handlePayByCash: () => handleBuy(WalletType.Cash),
			handlePayByCard: () => handleBuy(WalletType.Bank),
		};
	}

	// Покраска
	if (activeStage === TuningStudioStage.PAINT) {
		if (isBrakes) {
			const group = paintGroups.find(
				(item) => item.type === VehiclePaintType.Classic,
			);

			return {
				visible: true,
				name: 'Тормозные суппорта',
				price: group?.price || 0,
				handlePayByCash: () => handleBuy(WalletType.Cash),
				handlePayByCard: () => handleBuy(WalletType.Bank),
			};
		}

		if (isPaintingWheels) {
			const color = rimsColors.find((item) => item.index === rimsColor);

			return {
				visible: color !== undefined,
				name: color?.name || '',
				price: rimsColorsPrice,
				handlePayByCash: () => handleBuy(WalletType.Cash),
				handlePayByCard: () => handleBuy(WalletType.Bank),
			};
		}

		if (isPaintingInterior) {
			const group = paintGroups.find(
				(item) => item.type === VehiclePaintType.Classic,
			);

			return {
				visible: true,
				name: group?.name || 'Покраска салона',
				price: group?.price || 0,
				handlePayByCash: () => handleBuy(WalletType.Cash),
				handlePayByCard: () => handleBuy(WalletType.Bank),
			};
		}

		if (isPaintingWorks) {
			return {
				visible: activePaintGroup !== null,
				name: activePaintGroup?.name || '',
				price: activePaintGroup?.price || 0,
				handlePayByCash: () => handleBuy(WalletType.Cash),
				handlePayByCard: () => handleBuy(WalletType.Bank),
			};
		}
	}

	return {
		visible: false,
		name: '',
		price: 0,
		handlePayByCash: () => {},
		handlePayByCard: () => {},
	};
};

export { useShopPreview };
